<template>
  <div class="franchise-detail-page">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p class="loading-text">Loading franchise details...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="!franchise" class="error-container">
      <div class="empty-state">
        <h2>This franchise is on a business trip</h2>
        <p>Looks like this opportunity is currently unavailable. Let's explore other amazing options! ✈️</p>
        <router-link to="/franchises" class="retry-btn">Back to Franchises</router-link>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="franchise-content">
      <!-- Back Button -->
      <div class="container">
        <router-link to="/franchises" class="back-btn">
          <i class="fa fa-arrow-left"></i>
          Back to Franchises
        </router-link>
      </div>

      <!-- Hero Section -->
      <div class="hero-section">
        <div class="container">
          <div class="hero-content">
            <div class="hero-left">
              <div class="category-badge">
                {{ franchise.category || franchise.industry || 'N/A' }}
              </div>
              <h1 class="hero-title">{{ franchise.business_name || franchise.name || 'Franchise' }}</h1>
              <p class="hero-location">
                <i class="fa fa-map-marker-alt"></i>
                {{ franchise.headquarters || franchise.location || (Array.isArray(franchise.locations) ? franchise.locations[0] : '') || 'N/A' }}
              </p>
              <div class="franchise-meta">
                <span class="meta-item">
                  <i class="fa fa-calendar"></i>
                  Est. {{ franchise.establishment_year || franchise.established || 'N/A' }}
                </span>
                <span class="meta-item">
                  <i class="fa fa-store"></i>
                  {{ franchise.total_outlets || franchise.outlets || 0 }} Outlets
                </span>
                <span class="meta-item">
                  <i class="fa fa-tag"></i>
                  {{ franchise.model_type || 'Franchise' }}
                </span>
              </div>
            </div>
            <div class="investment-overview">
              <h3>Investment Overview</h3>
              <div class="overview-item">
                <span>Investment Range:</span>
                <span class="value">
                  {{ franchise ? formatInvestmentRange(franchise.investment_min, franchise.investment_max) : 'Loading...' }}
                </span>
              </div>
              <div class="overview-item">
                <span>Franchise Fee:</span>
                <span class="value">
                  {{ franchise && franchise.franchise_fee ? `₹${formatMoneyShort(franchise.franchise_fee)}` : franchise && franchise.fee ? franchise.fee : 'N/A' }}
                </span>
              </div>
              <div class="overview-item">
                <span>Expected ROI:</span>
                <span class="value">
                  {{ franchise ? formatRoiRange(franchise.excepted_roi_min, franchise.excepted_roi_max) : 'Loading...' }}
                </span>
              </div>
              <div class="overview-item">
                <span>Space Required:</span>
                <span class="value">{{ franchise.space_required || franchise.space || 'N/A' }}</span>
              </div>
              <div class="overview-item">
                <span>Royalty:</span>
                <span class="value">{{ franchise.royalty_percentage ? `${franchise.royalty_percentage}%` : 'N/A' }}</span>
              </div>
              <div class="overview-item">
                <span>Contract Duration:</span>
                <span class="value">{{ franchise.contract_duration ? `${franchise.contract_duration} months` : 'N/A' }}</span>
              </div>
              <button class="request-btn" @click="scrollToEnquiryForm">Request Information</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="container main-content">
        <div class="content-grid">
          <div class="content-left">
            <!-- Gallery -->
            <div class="gallery-section">
              <h2>Gallery</h2>
              <div class="gallery-grid">
                <div v-for="(image, index) in galleryImages" :key="index" 
                     :class="['gallery-item', { 'featured': index === 0 }]">
                  <img :src="image" :alt="`${franchise.business_name} - Image ${index + 1}`" />
                  <div v-if="index === 3 && franchise.images && franchise.images.length > 4" class="more-overlay">
                    <span>+{{ franchise.images.length - 4 }} more</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Description -->
            <div class="description-section">
              <h2>About {{ franchise.business_name || franchise.name }}</h2>
              <div class="description-content">
                <p>{{ franchise.description || 'No description available.' }}</p>
                
                <div class="support-training-grid">
                  <div class="support-section">
                    <h3><i class="fa fa-users"></i> Support Provided</h3>
                    <ul>
                      <li v-for="(item, index) in supportItems" :key="index">
                        <span class="check">✓</span>
                        <span>{{ item }}</span>
                      </li>
                    </ul>
                  </div>
                  <div class="training-section">
                    <h3><i class="fa fa-briefcase"></i> Training</h3>
                    <ul>
                      <li v-for="(item, index) in trainingItems" :key="index">
                        <span class="bullet">•</span>
                        <span>{{ item }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- Key Details -->
            <div class="key-details-section">
              <h2>Key Details</h2>
              <div class="key-details-content">
                <div class="details-grid">
                  <div class="detail-item">
                    <h3><i class="fa fa-chart-line"></i> Business Model</h3>
                    <p>{{ franchise.business_model || franchise.revenue_model || 'Standard franchise business model with proven track record.' }}</p>
                  </div>
                  <div class="detail-item">
                    <h3><i class="fa fa-clock"></i> Operating Hours</h3>
                    <p>{{ franchise.operating_hours || 'Flexible operating hours based on location and market demands.' }}</p>
                  </div>
                  <div class="detail-item">
                    <h3><i class="fa fa-users"></i> Target Market</h3>
                    <p>{{ franchise.target_market || 'Wide customer base across different demographics and market segments.' }}</p>
                  </div>
                  <div class="detail-item">
                    <h3><i class="fa fa-map-marker-alt"></i> Territory</h3>
                    <p>{{ franchise ? (formatTerritory(franchise.territory_details) || 'Exclusive territory rights based on location and market size.') : 'Loading...' }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Financial Information -->
            <div class="financial-section">
              <h2>Financial Information</h2>
              <div class="financial-content">
                <div class="financial-grid">
                  <div class="financial-item">
                    <h3>Initial Investment</h3>
                    <div class="financial-value">{{ franchise ? formatInvestmentRange(franchise.investment_min, franchise.investment_max) : 'Loading...' }}</div>
                    <p>Total startup costs including franchise fee, equipment, and working capital</p>
                  </div>
                  <div class="financial-item">
                    <h3>Franchise Fee</h3>
                    <div class="financial-value">{{ franchise && franchise.franchise_fee ? `₹${formatMoneyShort(franchise.franchise_fee)}` : 'Contact for details' }}</div>
                    <p>One-time fee for franchise rights and initial training</p>
                  </div>
                  <div class="financial-item">
                    <h3>Royalty Fee</h3>
                    <div class="financial-value">{{ franchise && franchise.royalty_percentage ? `${franchise.royalty_percentage}%` : 'Contact for details' }}</div>
                    <p>Ongoing percentage of gross sales paid to franchisor</p>
                  </div>
                  <div class="financial-item">
                    <h3>Marketing Fee</h3>
                    <div class="financial-value">{{ franchise && franchise.marketing_fee ? franchise.marketing_fee : 'Contact for details' }}</div>
                    <p>Contribution to national and local marketing campaigns</p>
                  </div>
                  <div class="financial-item">
                    <h3>Expected ROI</h3>
                    <div class="financial-value">{{ franchise ? formatRoiRange(franchise.excepted_roi_min, franchise.excepted_roi_max) : 'Loading...' }}</div>
                    <p>Projected return on investment timeline</p>
                  </div>
                  <div class="financial-item">
                    <h3>Contract Duration</h3>
                    <div class="financial-value">{{ franchise && franchise.contract_duration ? `${franchise.contract_duration} months` : 'Contact for details' }}</div>
                    <p>Length of franchise agreement</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Sidebar - Enquiry Form -->
          <div class="sidebar">
            <div class="enquiry-form">
              <h2>Request Information</h2>
              
              <!-- Success State -->
              <div v-if="submitted" class="success-state">
                <div class="success-icon">
                  <i class="fa fa-check"></i>
                </div>
                <h3>Enquiry Submitted!</h3>
                <p>Thank you for your interest in {{ franchise.business_name || franchise.name }}. A representative will contact you shortly.</p>
                <button @click="resetForm" class="reset-btn">Send Another Enquiry</button>
              </div>

              <!-- Form -->
              <form v-else @submit.prevent="submitEnquiry">
                <div class="form-group">
                  <label for="name">Your Name *</label>
                  <input 
                    id="name"
                    v-model="form.name" 
                    type="text" 
                    required 
                    @input="clearError('name')"
                  />
                  <span v-if="errors.name" class="error">{{ errors.name }}</span>
                </div>

                <div class="form-group">
                  <label for="email">Email Address *</label>
                  <input 
                    id="email"
                    v-model="form.email" 
                    type="email" 
                    required 
                    @input="clearError('email')"
                  />
                  <span v-if="errors.email" class="error">{{ errors.email }}</span>
                </div>

                <div class="form-group">
                  <label for="phone">Phone Number *</label>
                  <input 
                    id="phone"
                    v-model="form.phone" 
                    type="tel" 
                    required 
                    @input="clearError('phone')"
                  />
                  <span v-if="errors.phone" class="error">{{ errors.phone }}</span>
                </div>

                <div class="form-group">
                  <label for="message">Your Message</label>
                  <textarea 
                    id="message"
                    v-model="form.message" 
                    rows="4"
                    placeholder="Tell us about your interest in this franchise opportunity..."
                  ></textarea>
                </div>

                <div v-if="feedback" :class="['feedback', { 'success': submitted, 'error': !submitted }]">
                  {{ feedback }}
                </div>

                <button type="submit" class="submit-btn" :disabled="submitting">
                  {{ submitting ? 'Submitting...' : 'Submit Enquiry' }}
                </button>

                <p class="privacy-note">
                  By submitting this form, you agree to our privacy policy and terms of service.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useFranchiseStore } from '../stores/franchiseStore'
import api from '@/utils/api'

const route = useRoute()
const store = useFranchiseStore()
const franchise = ref(null)
const loading = ref(true)
const submitted = ref(false)
const submitting = ref(false)
const feedback = ref(null)
const errors = ref({})

const form = ref({ 
  name: '', 
  email: '', 
  phone: '', 
  message: '' 
})

// Computed properties
const galleryImages = computed(() => {
  if (!franchise.value) return []
  
  // Use business_logo as first image if available
  const images = []
  if (franchise.value.business_logo) {
    images.push(franchise.value.business_logo)
  }
  
  // Add other images
  if (Array.isArray(franchise.value.images)) {
    images.push(...franchise.value.images.filter(img => img))
  }
  
  return images.length > 0 ? images.slice(0, 4) : ['/placeholder.jpg']
})

const supportItems = computed(() => {
  if (!franchise.value) return []
  return Array.isArray(franchise.value.support_provided) 
    ? franchise.value.support_provided 
    : []
})

const trainingItems = computed(() => {
  if (!franchise.value) return []
  const training = franchise.value.training_details
  if (!training) return []
  
  if (Array.isArray(training)) return training
  return training.split(/\r?\n|,|\.|;/).filter(s => s.trim().length > 0)
})

// Methods
function validateForm() {
  const newErrors = {}
  
  if (!form.value.name.trim()) newErrors.name = 'Name is required'
  if (!form.value.email.trim()) newErrors.email = 'Email is required'
  else if (!validateEmail(form.value.email)) newErrors.email = 'Invalid email format'
  if (!form.value.phone.trim()) newErrors.phone = 'Phone is required'
  else if (!validatePhone(form.value.phone)) newErrors.phone = 'Invalid phone format'
  
  errors.value = newErrors
  return Object.keys(newErrors).length === 0
}

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function validatePhone(phone) {
  return /^[\+]?[1-9][\d]{0,15}$/.test(phone.replace(/\s/g, ''))
}

function clearError(field) {
  if (errors.value[field]) {
    errors.value = { ...errors.value, [field]: '' }
  }
}

function sanitizeInput(input) {
  return input.replace(/[<>]/g, '')
}

function formatMoneyShort(amount) {
  if (!amount) return '0'
  if (amount >= 10000000) return `${(amount / 10000000).toFixed(1)}Cr`
  if (amount >= 100000) return `${(amount / 100000).toFixed(1)}L`
  if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`
  return amount.toString()
}

function formatInvestmentRange(min, max) {
  if (!min && !max) return 'Contact for details'
  if (min && max) {
    return `₹${formatMoneyShort(min)} - ₹${formatMoneyShort(max)}`
  }
  if (min) {
    return `₹${formatMoneyShort(min)}`
  }
  if (max) {
    return `₹${formatMoneyShort(max)}`
  }
  return 'Contact for details'
}

function formatRoiRange(min, max) {
  if (!min && !max) return 'Contact for details'
  if (min && max) {
    return `${min} - ${max} months`
  }
  if (min) {
    return `${min} months`
  }
  if (max) {
    return `${max} months`
  }
  return 'Contact for details'
}

function formatTerritory(territoryDetails) {
  if (!territoryDetails) return null
  if (Array.isArray(territoryDetails)) {
    return territoryDetails.join(', ')
  }
  return territoryDetails
}

async function submitEnquiry() {
  if (!validateForm()) return
  
  feedback.value = null
  submitted.value = false
  submitting.value = true
  
  try {
    // Prepare the enquiry data
    const enquiryData = {
      franchise_id: franchise.value?.id,
      franchise_name: franchise.value?.business_name || franchise.value?.name,
      name: sanitizeInput(form.value.name),
      email: sanitizeInput(form.value.email),
      phone: sanitizeInput(form.value.phone),
      message: sanitizeInput(form.value.message),
      submitted_at: new Date().toISOString()
    }
    
    // Send data to backend using API service
    const response = await api.post('/enquiry', enquiryData)
    
    submitted.value = true
    feedback.value = 'Thank you for your enquiry! A representative will contact you shortly.'
    
    // Reset form
    form.value = { name: '', email: '', phone: '', message: '' }
    
  } catch (error) {
    
    let errorMessage = 'There was an error submitting your enquiry. Please try again later.'
    
    if (error.response) {
      // Server responded with error status
      if (error.response.status === 422) {
        errorMessage = 'Please check your form data and try again.'
      } else if (error.response.status === 409) {
        errorMessage = 'An enquiry with this information already exists.'
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message
      }
    } else if (error.message) {
      errorMessage = error.message
    }
    
    feedback.value = errorMessage
  } finally {
    submitting.value = false
  }
}

function resetForm() {
  submitted.value = false
  feedback.value = null
  errors.value = {}
}

function scrollToEnquiryForm() {
  const enquiryForm = document.querySelector('.enquiry-form');
  if (enquiryForm) {
    
    enquiryForm.scrollIntoView({ behavior: 'smooth' });
  }
}

onMounted(async () => {
  try {
    if (!store.franchises.length) {
      await store.fetchFranchises()
    }
    franchise.value = store.franchises.find(f => String(f.id) === String(route.params.id))
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.franchise-detail-page {
  min-height: 100vh;
  background: #f8fafc;
}

/* Loading State */
.loading-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 18px;
  color: #6b7280;
}

/* Error State */
.error-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state {
  text-align: center;
  max-width: 400px;
  padding: 40px 20px;
}

.empty-state h2 {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
}

.empty-state p {
  color: #6b7280;
  margin-bottom: 24px;
  line-height: 1.6;
}

.retry-btn {
  display: inline-block;
  background: #2563eb;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: background 0.2s;
}

.retry-btn:hover {
  background: #1d4ed8;
}

/* Container */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

/* Back Button */
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  color: #6b7280;
  text-decoration: none;
  font-weight: 500;
  margin: 24px 0;
  transition: color 0.2s;
}

.back-btn:hover {
  color: #2563eb;
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, #2563eb 0%, #4f46e5 100%);
  color: white;
  padding: 64px 0;
}

.hero-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;
}

.hero-left {
  flex: 1;
}

.category-badge {
  background: white;
  color: #2563eb;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  display: inline-block;
  margin-bottom: 16px;
}

.hero-title {
  font-size: 48px;
  font-weight: 800;
  margin-bottom: 16px;
  line-height: 1.2;
}

.hero-location {
  font-size: 18px;
  color: #dbeafe;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.franchise-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #dbeafe;
}

.meta-item i {
  font-size: 12px;
}

/* Investment Overview */
.investment-overview {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  color: #111827;
}

.investment-overview h3 {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 16px;
}

.overview-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 14px;
}

.overview-item span:first-child {
  color: #6b7280;
}

.overview-item .value {
  font-weight: 600;
  color: #111827;
}

.request-btn {
  width: 100%;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 16px;
}

.request-btn:hover {
  background: #1d4ed8;
}

/* Main Content */
.main-content {
  padding: 48px 0;
}

.content-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 32px;
}

/* Gallery */
.gallery-section {
  margin-bottom: 40px;
}

.gallery-section h2 {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 24px;
}

.gallery-grid {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  padding: 16px;
}

.gallery-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.gallery-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
}

.gallery-item.featured {
  grid-column: span 2;
  height: 400px;
}

.gallery-item:not(.featured) {
  height: 200px;
}

.gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.more-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  font-weight: 600;
}

/* Description */
.description-section {
  margin-bottom: 40px;
}

.description-section h2 {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 24px;
}

.description-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  padding: 24px;
}

.description-content p {
  color: #374151;
  line-height: 1.7;
  margin-bottom: 32px;
  font-size: 16px;
}

.support-training-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.support-section h3,
.training-section h3 {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.support-section h3 i,
.training-section h3 i {
  color: #2563eb;
}

.support-section ul,
.training-section ul {
  list-style: none;
  padding: 0;
}

.support-section li,
.training-section li {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  color: #374151;
}

.support-section .check {
  color: #10b981;
  margin-right: 8px;
  font-weight: 600;
}

.training-section .bullet {
  color: #2563eb;
  margin-right: 8px;
  font-weight: 600;
}

/* Key Details */
.key-details-section {
  margin-bottom: 40px;
}

.key-details-section h2 {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 24px;
}

.key-details-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  padding: 24px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
}

.detail-item h3 {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.detail-item h3 i {
  color: #2563eb;
}

.detail-item p {
  color: #6b7280;
  font-size: 14px;
  line-height: 1.6;
}

/* Sidebar */
.sidebar {
  position: relative;
}

.enquiry-form {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  padding: 24px;
  position: sticky;
  top: 96px;
}

.enquiry-form h2 {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 16px;
}

/* Success State */
.success-state {
  text-align: center;
  padding: 40px 20px;
}

.success-icon {
  background: #dcfce7;
  border-radius: 50%;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.success-icon i {
  color: #16a34a;
  font-size: 32px;
}

.success-state h3 {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

.success-state p {
  color: #6b7280;
  margin-bottom: 16px;
  line-height: 1.6;
}

.reset-btn {
  background: transparent;
  border: 1px solid #d1d5db;
  color: #374151;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.reset-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

/* Form */
.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.error {
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.feedback {
  padding: 12px;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 16px;
}

.feedback.success {
  background: #dcfce7;
  color: #166534;
}

.feedback.error {
  background: #fef2f2;
  color: #dc2626;
}

.submit-btn {
  width: 100%;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.submit-btn:hover:not(:disabled) {
  background: #1d4ed8;
}

.submit-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.privacy-note {
  font-size: 12px;
  color: #6b7280;
  margin-top: 16px;
  text-align: center;
}

/* Responsive */
@media (max-width: 1024px) {
  .hero-content {
    flex-direction: column;
    text-align: center;
  }
  
  .investment-overview {
    max-width: 100%;
  }
  
  .content-grid {
    grid-template-columns: 1fr;
  }
  
  .enquiry-form {
    position: static;
  }
  
  .support-training-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 0 12px;
  }
  
  .hero-title {
    font-size: 32px;
  }
  
  .gallery-grid {
    grid-template-columns: 1fr;
  }
  
  .gallery-item.featured {
    grid-column: span 1;
    height: 200px;
  }
  
  .hero-section {
    padding: 40px 0;
  }
  
  .main-content {
    padding: 32px 0;
  }
  
  .details-grid,
  .financial-grid {
    grid-template-columns: 1fr;
  }
  
  .financial-item {
    padding: 16px;
  }
  
  .detail-item h3,
  .financial-item h3 {
    font-size: 14px;
  }
  
  .financial-value {
    font-size: 18px;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 28px;
  }
  
  .investment-overview {
    padding: 16px;
  }
  
  .enquiry-form {
    padding: 16px;
  }
  
  .description-content,
  .key-details-content {
    padding: 16px;
  }
  
  .key-details-content,
  .financial-content {
    padding: 16px;
  }
  
  .detail-item h3,
  .financial-item h3 {
    font-size: 14px;
  }
  
  .financial-value {
    font-size: 18px;
  }
}

/* Financial Section */
.financial-section {
  margin-bottom: 40px;
}

.financial-section h2 {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 24px;
}

.financial-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  padding: 24px;
}

.financial-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.financial-item {
  padding: 20px;
  border-radius: 8px;
  background: #f8fafc;
  text-align: center;
}

.financial-item h3 {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
}

.financial-value {
  font-size: 20px;
  font-weight: 700;
  color: #2563eb;
  margin-bottom: 8px;
}

.financial-item p {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.4;
}
</style> 
